import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ApiService } from 'services/ApiService';
import {
  WorkflowStartResponse,
  isWaitingForInput,
  isWorkflowCompleted,
  EnrollmentResponseDto,
} from 'models/api/WorkflowStartResponse';
import { EnrollmentView } from 'components/enrollments/EnrollmentView';
import { FormEnrollment } from 'models/enrollments/FormEnrollment';
import { ReviewEnrollment } from 'models/enrollments/ReviewEnrollment';
import { EventEnrollment } from 'models/enrollments/EventEnrollment';
import type { Enrollment } from 'models/enrollments/Enrollment';
import type { ReviewDecisionSubmission } from 'models/submissions/ReviewDecisionSubmission';
import type { FormSubmission } from 'models/submissions/FormSubmission';
import type { Submission } from 'models/submissions/Submission';
import type { UserSubmissionDto } from 'models/projects/UserSubmissionDto';
import { utilities } from 'utils/utilities';

type PageState =
  | { status: 'loading' }
  | { status: 'waitingForInput'; response: EnrollmentResponseDto; enrollment: Enrollment }
  | { status: 'completed'; workflowInstanceId: string; output?: string }
  | { status: 'error'; message: string };

function enrollmentFromDto(dto: EnrollmentResponseDto): Enrollment {
  const { enrollmentId, enrollmentType, formId, assignedTeamId, eventName } = dto;
  switch (enrollmentType) {
    case 'FormEnrollment':
      return new FormEnrollment(enrollmentId, formId ?? utilities.DEFAULT_GUID);
    case 'ReviewEnrollment':
      return new ReviewEnrollment(enrollmentId, assignedTeamId ?? utilities.DEFAULT_GUID);
    case 'EventEnrollment':
      return new EventEnrollment(enrollmentId, eventName ?? '');
    default:
      // Unrecognised — fall back to FormEnrollment shape so the user sees something
      return new FormEnrollment(enrollmentId, formId ?? utilities.DEFAULT_GUID);
  }
}

function applyResponse(response: WorkflowStartResponse): PageState {
  if (isWorkflowCompleted(response)) {
    return {
      status: 'completed',
      workflowInstanceId: response.workflowInstanceId,
      output: response.output,
    };
  }
  if (isWaitingForInput(response)) {
    return {
      status: 'waitingForInput',
      response,
      enrollment: enrollmentFromDto(response),
    };
  }
  return { status: 'error', message: 'Unexpected response from server.' };
}

const NodePage: React.FC = () => {
  const { workflowTemplateId, teamId } = useParams<{
    workflowTemplateId: string;
    teamId: string;
  }>();

  const [pageState, setPageState] = useState<PageState>({ status: 'loading' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const startedRef = useRef(false);

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;

    if (!workflowTemplateId || !teamId) {
      setPageState({ status: 'error', message: 'Missing workflow or team identifier.' });
      return;
    }

    ApiService.startWorkflow(workflowTemplateId, teamId)
      .then((response) => setPageState(applyResponse(response)))
      .catch((err) => {
        console.error('startWorkflow failed:', err);
        setPageState({
          status: 'error',
          message:
            err?.response?.data?.errors?.[0] ??
            'Failed to start the workflow. Please try again.',
        });
      });
  }, [workflowTemplateId, teamId]);

  const submitToEnrollment = useCallback(
    async (enrollmentId: string, submission: Submission) => {
      setIsSubmitting(true);
      try {
        const response = await ApiService.submitToEnrollment(enrollmentId, submission);
        setPageState(applyResponse(response));
      } catch (err: any) {
        console.error('submitToEnrollment failed:', err);
        setPageState({
          status: 'error',
          message:
            err?.response?.data?.errors?.[0] ?? 'Submission failed. Please try again.',
        });
      } finally {
        setIsSubmitting(false);
      }
    },
    [],
  );

  const handleSubmitReviewDecision = useCallback(
    (submission: ReviewDecisionSubmission) => {
      if (pageState.status !== 'waitingForInput') return;
      submitToEnrollment(pageState.response.enrollmentId, submission as Submission);
    },
    [pageState, submitToEnrollment],
  );

  const handleFormSubmit = useCallback(
    (submissionDto: UserSubmissionDto) => {
      if (pageState.status !== 'waitingForInput') return;

      const formSubmission: FormSubmission = {
        id: utilities.DEFAULT_GUID,
        enrollmentId: pageState.response.enrollmentId,
        nodeInstanceId: utilities.DEFAULT_GUID,
        workflowInstanceId: pageState.response.workflowInstanceId,
        submittedByUserId: '',
        type: 'FormSubmission',
        submittedAt: new Date().toISOString(),
        responseJson: JSON.stringify(submissionDto),
      };

      submitToEnrollment(pageState.response.enrollmentId, formSubmission);
    },
    [pageState, submitToEnrollment],
  );

  // ── Loading ──────────────────────────────────────────────────────────────────
  if (pageState.status === 'loading') {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
          <p className="text-gray-500 text-sm">Starting workflow…</p>
        </div>
      </div>
    );
  }

  // ── Error ────────────────────────────────────────────────────────────────────
  if (pageState.status === 'error') {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="max-w-md w-full bg-white border border-red-200 rounded-lg p-8 shadow-sm">
          <h2 className="text-lg font-semibold text-red-600 mb-2">Something went wrong</h2>
          <p className="text-gray-600 text-sm">{pageState.message}</p>
        </div>
      </div>
    );
  }

  // ── Completed ────────────────────────────────────────────────────────────────
  if (pageState.status === 'completed') {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="max-w-md w-full bg-white border border-green-200 rounded-lg p-8 shadow-sm">
          <h2 className="text-xl font-semibold text-green-700 mb-2">Workflow Complete</h2>
          <p className="text-gray-500 text-xs mb-4">
            Instance ID:{' '}
            <code className="bg-gray-100 px-1.5 py-0.5 rounded">
              {pageState.workflowInstanceId}
            </code>
          </p>
          {pageState.output && (
            <div className="mt-4">
              <h3 className="text-sm font-medium text-gray-700 mb-1">Output</h3>
              <pre className="text-xs bg-gray-50 border rounded p-3 overflow-auto whitespace-pre-wrap">
                {pageState.output}
              </pre>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ── Waiting for input ────────────────────────────────────────────────────────
  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-50 p-4">
      <div className="max-w-2xl w-full">
        <EnrollmentView
          enrollment={pageState.enrollment}
          isDisabled={isSubmitting}
          onSubmit={handleFormSubmit}
          onSubmitReviewDecision={handleSubmitReviewDecision}
        />
        {isSubmitting && (
          <div className="flex justify-center mt-4">
            <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
          </div>
        )}
      </div>
    </div>
  );
};

export default NodePage;
